import { ContentState, SelectionState, Modifier } from 'draft-js';
import { WordInfo } from '../models';
import { abbrs_dotted } from '../abbrs';
import { toRoman, romanContentBoundaryRuleDefs, romanIncomingMsgBoundaryRuleDefs } from './utils';

const abbrPattern: RegExp = new RegExp(`(${abbrs_dotted.map((a) => a.replace(/\./g, '\\.')).join('|')}) $`, 'ui');

const fixContentBoundarySpacing = (
  initialContentState: ContentState,
  initialSelection: SelectionState,
  nextWordInfo?: WordInfo,
): ContentState => {
  // Fix boundary between existing content and incoming content.
  // Alters content state.

  // Delete the preceding space if
  // a) the next word begins with a "space consuming" punctuation token or
  // b) the next word starts with a digit and last word ended with an arithmetic symbol like "x".

  const anchorKey = initialSelection.getAnchorKey();
  const currentBlock = initialContentState.getBlockForKey(anchorKey);

  if (initialSelection.isCollapsed()) {
    const endInspect = initialSelection.getStartOffset();

    const SPACE_REMOVAL_RULES = [
      {
        // Remove space before punctuation:
        // "foo <>. bar " → "foo. bar "
        // "eftirfarandi <>: " → "eftirfarandi: "
        nextWordPattern: /^[:.\n,×x!?°%)/]/,
        precedingChars: [' '],
      },
      {
        // Remove space before digit when preceded by operator or separator and a space:
        // "12,5x <>3,7 " → "12,5x3,7 "
        // "13, <>9 " → "13,9 "
        nextWordPattern: /^[0-9]/,
        precedingChars: ['x ', '× ', ', '],
      },
    ];

    /*
      More verbose example of a rule application:
      [
        {
          nextWordPattern: /^[0-9]/,            // If next incoming word starts with this pattern...
          precedingChars: ['x ', '× ', ', '],   // ...and the last characters of existing content (behind cursor) are one of those strings...
        },                                      // ...then we remove a single space at the end of the existing content and move cursor back by one.
      ]                                         // This ensures that "...mælist 3x <>5mm..." -> "...mælist 3x5mm..." rather than "...mælist 3x 5mm...".
                                                // This ensures that "...mælist 3, <>5mm..." -> "...mælist 3,5mm..." rather than "...mælist 3, 5mm...".
                                                // etc.
      */

    const startRm: number = initialSelection.getStartOffset() - 1;
    const endRm: number = endInspect;

    const nextWord: string = nextWordInfo?.word ? nextWordInfo?.word : '';
    for (const rule of SPACE_REMOVAL_RULES) {
      // NOTE: All precedingChars must be of the same length. This is worth a refactor to prevent future bugs.
      const precChrsLength: number = rule.precedingChars[0].length;
      const guard: boolean = endInspect >= precChrsLength;
      if (nextWord.match(rule.nextWordPattern) && guard) {
        const startInspect: number = endInspect - precChrsLength;
        if (rule.precedingChars.includes(currentBlock.getText().slice(startInspect, endInspect))) {
          return Modifier.removeRange(
            initialContentState,
            SelectionState.createEmpty(currentBlock.getKey()).merge({
              anchorOffset: startRm,
              focusOffset: endRm,
            }) as SelectionState,
            'backward',
          ).set(
            'selectionAfter',
            SelectionState.createEmpty(currentBlock.getKey()).merge({
              anchorOffset: initialSelection.getStartOffset() - 1,
              focusOffset: initialSelection.getEndOffset() - 1,
            }) as SelectionState,
          ) as ContentState;
        }
      }
    }
  }

  // Leave as is if no rule applies.
  return initialContentState.set(
    'selectionAfter',
    SelectionState.createEmpty(currentBlock.getKey()).merge({
      anchorOffset: initialSelection.getStartOffset(),
      focusOffset: initialSelection.getEndOffset(),
    }) as SelectionState,
  ) as ContentState;
};

const fixIncomingBoundarySpacing = (
  initialContentState: ContentState,
  initialSelection: SelectionState,
  wordInfos: WordInfo[],
) => {
  // Fixes spacing within incoming content.
  // NOTE: Assumes that editor content has already been fixed via fixContentBoundarySpacing.

  const wordInfosNormalized: WordInfo[] = [...wordInfos];
  // console.log(`wordInfos: ${JSON.stringify(wordInfosNormalized, null, 2)}`); // Uncomment me for debugging, I'm useful.

  const anchorKey = initialSelection.getAnchorKey();
  const currentBlock = initialContentState.getBlockForKey(anchorKey);

  if (initialSelection.isCollapsed()) {
    const endInspect = initialSelection.getStartOffset();

    const SPACE_REMOVAL_RULES = [
      {
        // Remove space before digit when preceded by operator or separator and a space:
        // "3<>, 5 " → "3,5 "
        precedingWordPattern: /\d$/,
        nextWordPattern: /^, \d/,
      },
    ];

    const nextWordsAll: string = wordInfos.map((wrd) => wrd.word).join('');
    for (const rule of SPACE_REMOVAL_RULES) {
      if (nextWordsAll.match(rule.nextWordPattern)) {
        if (currentBlock.getText().slice(0, endInspect).match(rule.precedingWordPattern)) {
          wordInfosNormalized[0] = { ...wordInfosNormalized[0], word: wordInfosNormalized[0].word.trimEnd() };
        }
      }
    }
  }
  return wordInfosNormalized;
};

const enforceCapitalization = (
  initialContentState: ContentState,
  initialSelection: SelectionState,
  wordInfos: WordInfo[],
) => {
  // Enforce correct capitalization of incoming message strings:
  // a) Boundarywise: Capitalize first character of incoming string based on existing content state.
  //      Example: "...að svo stöddu. <>nýjustu mælingar sýna..." -> "...að svo stöddu. <>Nýjustu mælingar sýna..."
  // b) Internally: Capitalize internal characters of incoming strings based on internal characters.
  //      Example: "...óvíst <>hvort það verði gert. sprunga distalt á..." -> "...óvíst <>hvort það verði gert. Sprunga distalt á..."

  if (!wordInfos || !initialSelection.isCollapsed()) {
    // No incoming text to capitalize or noncollapsed selection.
    // Leave as is.
    return wordInfos;
  }

  const wordInfosCapitalized: WordInfo[] = [...wordInfos];
  const capitalize = (word: string) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`;

  if (!initialContentState.hasText()) {
    // Make sure first character of first segment is always capitalized.
    wordInfosCapitalized[0] = {
      ...wordInfosCapitalized[0],
      word: capitalize(wordInfosCapitalized[0].word),
    };
    return wordInfosCapitalized;
  }

  const startAlphaLowerPattern: RegExp = /^\p{Ll}/u;
  const CAPITALIZATION_RULES = [
    {
      // Example:
      //  "maður. <>mæling " -> "maður. <>Mæling "
      //       ^^^
      //  "3. <>mæling " -> "3. <>mæling "          // We do NOT capitalize here as the dot MIGHT indicate an ordinal number.
      //   ^^^
      precedingLength: 3,
      precedingPattern: /\p{Ll}\. $/u, // Lower-case alphabetic letter, a dot, a whitespace and end of string.
      negatingPattern: abbrPattern,
      nextWordPattern: startAlphaLowerPattern,
    },
    {
      // Example:
      //  "þensla\n<>mæling " -> "þensla\n<>Mæling "
      //         ^^
      precedingLength: 1,
      precedingPattern: /\n$/, // A newline and end of string.
      negatingPattern: undefined,
      nextWordPattern: startAlphaLowerPattern,
    },
    {
      // Example:
      //  "þensla? <>mæling " -> "þensla? <>Mæling "
      //         ^^
      precedingLength: 2,
      precedingPattern: /\? $/, // A question mark, a whitespace and end of string.
      negatingPattern: undefined,
      nextWordPattern: startAlphaLowerPattern,
    },
    {
      //  "\n- <>frekari rannsóknir " -> "\n- <>Frekari rannsóknir "
      //   ^^^^
      precedingLength: 3,
      precedingPattern: /\n- $/, // A newline, a hyphen, a whitespace and end of string.
      negatingPattern: undefined,
      nextWordPattern: startAlphaLowerPattern,
    },
  ];

  const anchorKey = initialSelection.getAnchorKey();
  const currentBlock = initialContentState.getBlockForKey(anchorKey);

  const endInspect = initialSelection.getStartOffset();

  // Boundarywise capitalization.
  for (const rule of CAPITALIZATION_RULES) {
    const startInspect: number = endInspect - rule.precedingLength;
    if (startInspect < 0) continue;

    // For the abbr. blacklist check, we need to look at the whole content
    // as abbreviations can vary in length. The negating pattern is anchored
    // to the end of the string via '$'.
    const precedingContent: string = currentBlock.getText().slice(0, endInspect);
    if (rule.negatingPattern?.test(precedingContent)) continue;

    const precedingWord: string = currentBlock.getText().slice(startInspect, endInspect);
    if (precedingWord.match(rule.precedingPattern) && wordInfosCapitalized[0].word.match(rule.nextWordPattern)) {
      wordInfosCapitalized[0] = {
        ...wordInfosCapitalized[0],
        word: capitalize(wordInfosCapitalized[0].word),
      };
    }
  }

  // WordInfo array internal capitalization.
  for (let wIdx = 0; wIdx < wordInfosCapitalized.length; wIdx++) {
    if (wIdx === 0) {
      // We have already dealt with the content/wordInfos boundary. Skip.
      continue;
    }

    const precedingWord: string = wordInfosCapitalized[wIdx - 1].word;
    const currentWord: string = wordInfosCapitalized[wIdx].word;
    for (const rule of CAPITALIZATION_RULES) {
      if (rule.negatingPattern?.test(precedingWord)) continue;
      if (precedingWord.match(rule.precedingPattern) && currentWord.match(rule.nextWordPattern)) {
        wordInfosCapitalized[wIdx] = {
          ...wordInfosCapitalized[wIdx],
          word: capitalize(wordInfosCapitalized[wIdx].word),
        };

        // More than one rule cannot apply at once, so we quit if one applies.
        break;
      }
    }
  }

  return wordInfosCapitalized;
};

const fixContentBoundaryRomanisation = (
  initialContentState: ContentState,
  initialSelection: SelectionState,
  nextWordInfos: WordInfo[],
): ContentState => {
  //  Enforce correct transformation of Arabic numbers to Roman numbers at the end of existing content.
  //  Capitalize last Arabic numeric word(s) of existing content based on context at the start of incoming string.
  //  Example: "...um að ræða 3. og 4. <>fingur en það eru..." -> "...um að ræða III. og IV. <>fingur en það eru..."
  //  Example: "...um að ræða 3. og <>IV. fingur en það eru..." -> "...um að ræða III. og <>IV. fingur en það eru..."
  const anchorKey = initialSelection.getAnchorKey();
  const currentBlock = initialContentState.getBlockForKey(anchorKey);

  if (initialSelection.isCollapsed()) {
    const endInspect = initialSelection.getStartOffset();

    // NOTE: These rules are fully disambiguated by the incomingMsgPatterns but not by the precedingPatterns.
    //       ptnArabNumPartial CAN match a full sequence as well as a partial sequence but
    //       ptnArabNumFull CANNOT match a partial sequence, only a full sequence.
    //       As a consequence, rule order matters.
    //       Please bear this in mind if you intend to add rules here.

    // We use the context pattern to see if the incoming message starts with a context that invites a conversion of
    // Arabic numbers to Roman numbers, should they be present at the end of the editor content.
    // We use the Arabic numeral patterns to check if there are Arabic numbers present at the end of the editor content.
    const RULES = [
      {
        // Rule #1:
        // Content romanization - Whole sequence or a single number.
        // Example: "...um að ræða 3. og 4. <>fingur en það eru..." -> "...um að ræða III. og IV. <>fingur en það eru..."
        // Example: "...um að ræða 3. <>fingur en það eru..." -> "...um að ræða III. <>fingur en það eru..."
        precedingPattern: romanContentBoundaryRuleDefs.first.precedingPattern,
        incomingMsgPattern: romanContentBoundaryRuleDefs.first.incomingMsgPattern,
      },
      {
        // Rule #2:
        // Content romanization - Partial sequence.
        // Example: "...um að ræða 3. og <>IV. fingur en það eru..." -> "...um að ræða III. og <>IV. fingur en það eru..."
        precedingPattern: romanContentBoundaryRuleDefs.second.precedingPattern,
        incomingMsgPattern: romanContentBoundaryRuleDefs.second.incomingMsgPattern,
      },
    ];

    // const startRm: number = initialSelection.getStartOffset() - 1;
    // const endRm: number = endInspect;

    const precedingContent: string = currentBlock.getText().slice(0, endInspect);
    const incomingMsg: string = nextWordInfos.length > 0 ? nextWordInfos.map((nfo: WordInfo) => nfo.word).join('') : '';
    for (const rule of RULES) {
      if (rule.incomingMsgPattern.test(incomingMsg)) {
        // Context presence established at start of incoming message.

        const pCMatch = precedingContent.match(rule.precedingPattern);
        if (pCMatch) {
          // Arabic numeral presence established at end of content.

          // The matching trailing end of the content.
          const pCTrailingArabics: string = pCMatch![0];
          const startCut: number = pCMatch.index!;
          const blockKey: string = currentBlock.getKey();

          // Rewrite each Arabic numeral token in place, reusing the SYNC_WORD entity it
          // already carries so the word's startTime/endTime are preserved. We walk the
          // tokens right-to-left so that length changes (e.g. "3" -> "III") never shift
          // the offsets of tokens we have not processed yet.
          const digitTokens = [
            ...pCTrailingArabics.matchAll(new RegExp(String.raw`(?<!\p{L})[0-9]+(?!\p{L})`, 'gu')),
          ].reverse();

          let modifiedState: ContentState = initialContentState;
          let delta = 0; // Net length change, used to place the cursor afterwards.
          for (const token of digitTokens) {
            const absStart: number = startCut + token.index!;
            const absEnd: number = absStart + token[0].length;
            const roman: string = toRoman(Number(token[0]));

            // Read the entity from the original block (earlier offsets are still valid).
            const entityKey: string | null = currentBlock.getEntityAt(absStart);

            const tokenRange = SelectionState.createEmpty(blockKey).merge({
              anchorOffset: absStart,
              focusOffset: absEnd,
            }) as SelectionState;

            modifiedState = Modifier.replaceText(modifiedState, tokenRange, roman, undefined, entityKey ?? undefined);
            if (entityKey) {
              // The ASR entity word carries the ordinal period and trailing space (e.g. "5. "),
              // and the period/space are left untouched in the block. Romanise only the digits of
              // the stored word so its data stays consistent with the text the entity now spans.
              const before = modifiedState.getEntity(entityKey).getData();
              const romanWord: string = before.word.replace(/(?<!\p{L})[0-9]+(?!\p{L})/gu, (d: string) =>
                toRoman(Number(d)),
              );
              modifiedState = modifiedState.mergeEntityData(entityKey, { word: romanWord });
            }
            delta += roman.length - token[0].length;
          }

          // Cursor lands at the end of content, where the incoming words get appended.
          const caret: number = endInspect + delta;
          return modifiedState.set(
            'selectionAfter',
            SelectionState.createEmpty(blockKey).merge({
              anchorOffset: caret,
              focusOffset: caret,
            }) as SelectionState,
          ) as ContentState;
        }
      }
    }
  }

  // Leave as is if no rule applies.
  return initialContentState.set(
    'selectionAfter',
    SelectionState.createEmpty(currentBlock.getKey()).merge({
      anchorOffset: initialSelection.getStartOffset(),
      focusOffset: initialSelection.getEndOffset(),
    }) as SelectionState,
  ) as ContentState;
};

const fixIncomingBoundaryRomanisation = (
  initialContentState: ContentState,
  initialSelection: SelectionState,
  nextWordInfos: WordInfo[],
) => {
  // Enforce correct transformation of Arabic numbers to Roman numbers at start of incoming message strings:
  // Transform Arabic numeric word(s) at start of incoming string based on context at the end of existing content state.
  //      Example: "...um að ræða fingur <>3 og 4 en það eru..." -> "...um að ræða fingur <>III og IV en það eru..."
  //      Example: "...um að ræða fingur III og <>4 en það eru..." -> "...um að ræða fingur III og <>IV en það eru..."
  if (!nextWordInfos || !initialSelection.isCollapsed()) {
    // No incoming text to capitalize or noncollapsed selection.
    // Leave as is.
    return nextWordInfos;
  }

  const wordInfosRomanized: WordInfo[] = [...nextWordInfos];

  const anchorKey = initialSelection.getAnchorKey();
  const currentBlock = initialContentState.getBlockForKey(anchorKey);
  const endInspect = initialSelection.getStartOffset();

  const RULES = [
    {
      // Rule #1:
      // Incoming text romanization - Beginning of a sequence or a single number.
      // Example: "...um að ræða fingur <>3 en það eru..." -> "...um að ræða fingur <>III en það eru..."
      // Example: "...um að ræða fingur <>3 og 4 en það eru..." -> "...um að ræða fingur <>III og IV en það eru..."
      precedingPattern: romanIncomingMsgBoundaryRuleDefs.first.precedingPattern,
      incomingMsgPattern: romanIncomingMsgBoundaryRuleDefs.first.incomingMsgPattern,
    },
    {
      // Rule #2:
      // Incoming text romanization - Partial sequence.
      // Example: "...um að ræða fingur III og <>4 en það eru..." -> "...um að ræða fingur III og <>IV en það eru..."
      precedingPattern: romanIncomingMsgBoundaryRuleDefs.second.precedingPattern,
      incomingMsgPattern: romanIncomingMsgBoundaryRuleDefs.second.incomingMsgPattern,
    },
  ];

  const precedingContent: string = currentBlock.getText().slice(0, endInspect);
  const incomingMsg: string = wordInfosRomanized.map((nfo: WordInfo) => nfo.word).join('');
  for (const rule of RULES) {
    const incomingMatch = incomingMsg.match(rule.incomingMsgPattern);
    if (incomingMatch) {
      if (rule.precedingPattern.test(precedingContent)) {
        const convOffsetMax: number = incomingMatch[0].length;
        let cursor: number = 0;
        for (let i = 0; i < wordInfosRomanized.length; i++) {
          if (cursor >= convOffsetMax) break;
          const offsetJump: number = wordInfosRomanized[i].word.length;
          wordInfosRomanized[i] = {
            ...wordInfosRomanized[i],
            word: wordInfosRomanized[i].word.replace(/(?<!\p{L})[0-9]+(?!\p{L})/gu, (d: string) => toRoman(Number(d))),
          };
          cursor += offsetJump;
        }
        break;
      }
    }
  }
  return wordInfosRomanized;
};

// ── Pipeline ─────────────────────────────────────────────────────────────────
// A segment in flight through post-processing: existing editor content, the cursor
// where incoming words will be inserted, and the incoming words themselves.
export interface SegmentDraft {
  contentState: ContentState;
  selection: SelectionState;
  wordInfos: WordInfo[];
}

type PostProcessStep = (draft: SegmentDraft) => SegmentDraft;

// Adapts a transformer that rewrites the existing CONTENT (returns a new ContentState
// whose selectionAfter is the updated cursor).
const contentStep =
  (fn: (cs: ContentState, sel: SelectionState, wordInfos: WordInfo[]) => ContentState): PostProcessStep =>
  (draft: SegmentDraft) => {
    const contentState = fn(draft.contentState, draft.selection, draft.wordInfos);
    return { ...draft, contentState, selection: contentState.getSelectionAfter() };
  };

// Adapts a transformer that rewrites the incoming WORDINFOS.
const wordInfoStep =
  (fn: (cs: ContentState, sel: SelectionState, wordInfos: WordInfo[]) => WordInfo[]): PostProcessStep =>
  (draft: SegmentDraft) => ({ ...draft, wordInfos: fn(draft.contentState, draft.selection, draft.wordInfos) });

// Ordered steps applied to each incoming segment before its words are committed as
// entities. To add a step, insert it here with the matching adapter — order matters
// (e.g. capitalization must run before romanisation).
const POST_PROCESS_PIPELINE: PostProcessStep[] = [
  contentStep((cs, sel, wordInfos) => fixContentBoundarySpacing(cs, sel, wordInfos[0])),
  wordInfoStep(fixIncomingBoundarySpacing),
  wordInfoStep(enforceCapitalization),
  contentStep(fixContentBoundaryRomanisation),
  wordInfoStep(fixIncomingBoundaryRomanisation),
];

export const runSegmentPostProcess = (draft: SegmentDraft): SegmentDraft =>
  POST_PROCESS_PIPELINE.reduce((acc, step) => step(acc), draft);
